import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, InlineLoading } from "@carbon/react";
import { WarningAlt } from "@carbon/icons-react";
import {
  OfferAssistantMessage,
  OfferAssistantRecommendation,
} from "../../types";
import SimpleMarkdown from "./SimpleMarkdown";

type Props = {
  messages: OfferAssistantMessage[];
  /** Shows a recommendation from an earlier answer again (marks only, no pre-selection). */
  onShowRecommendation: (recommendation: OfferAssistantRecommendation) => void;
};

const AssistantBody = ({
  message,
  onShowRecommendation,
}: {
  message: OfferAssistantMessage;
  onShowRecommendation: Props["onShowRecommendation"];
}) => {
  const { t } = useTranslation();

  if (message.status === "PENDING") {
    return <InlineLoading description={t("offerAssistant.thinking")} />;
  }
  if (message.status === "FAILED") {
    return (
      <p className="offer-assistant__failed">
        <WarningAlt size={16} aria-hidden="true" />
        {t("offerAssistant.failed")}
      </p>
    );
  }
  return (
    <>
      {message.text && <SimpleMarkdown text={message.text} />}
      {message.recommendation && (
        <Button
          kind="ghost"
          size="sm"
          className="offer-assistant__show-rec"
          onClick={() => onShowRecommendation(message.recommendation!)}
        >
          {t("offerAssistant.recommendation.show")}
        </Button>
      )}
    </>
  );
};

/** The chat transcript: the buyer's turns on the right, the assistant's on the left. */
const OfferAssistantMessages = ({ messages, onShowRecommendation }: Props) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLOListElement>(null);

  // Keep the newest turn in view as messages arrive or a pending reply resolves.
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages]);

  if (messages.length === 0) {
    return (
      <p className="offer-assistant__empty">{t("offerAssistant.empty")}</p>
    );
  }

  return (
    <ol
      ref={listRef}
      className="offer-assistant__messages"
      aria-label={t("offerAssistant.transcript")}
      aria-live="polite"
    >
      {messages.map((message) => {
        const isUser = message.role === "USER";
        return (
          <li
            key={message.id}
            className={`offer-assistant__bubble offer-assistant__bubble--${
              isUser ? "user" : "assistant"
            }`}
          >
            <span className="offer-assistant__author">
              {isUser ? t("offerAssistant.you") : t("offerAssistant.assistant")}
            </span>
            {isUser ? (
              <p className="offer-assistant__user-text">{message.text}</p>
            ) : (
              <AssistantBody
                message={message}
                onShowRecommendation={onShowRecommendation}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default OfferAssistantMessages;
